// First Created - 12 Jun 2026
// Author - Atul Pandey
// Purpose - API slice for Org Trail Session Cohorts
// Updated - 15 Jun 2026 - builder endpoint for default cohort

import {
    createSelector,
    createEntityAdapter,
} from '@reduxjs/toolkit'
import { apiSlice } from '@/app/api/apiSlice'

const orgTrailSessionCohortsAdapter = createEntityAdapter({})

const initialState = orgTrailSessionCohortsAdapter.getInitialState()

export const orgTrailSessionCohortsApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getOrgTrailSessionCohorts: builder.query({
            query: ({
                orgId = '',
                trailId = '',
                sessionId = '',
                cohortStatus = '',
                active = '',
            } = {}) => ({
                url: '/orgTrailSessionCohorts/getOrgTrailSessionCohorts',
                params: {
                    ...(orgId ? { orgId } : {}),
                    ...(trailId ? { trailId } : {}),
                    ...(sessionId ? { sessionId } : {}),
                    ...(cohortStatus ? { cohortStatus } : {}),
                    ...(active !== '' ? { active } : {}),
                },
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },
            }),
            transformResponse: responseData => {
                const loadedOrgTrailSessionCohorts = responseData.map(cohort => {
                    cohort.id = cohort._id
                    return cohort
                })

                return orgTrailSessionCohortsAdapter.setAll(
                    initialState,
                    loadedOrgTrailSessionCohorts
                )
            },
            providesTags: (result, error, arg) => {
                if (result?.ids) {
                    return [
                        { type: 'OrgTrailSessionCohort', id: 'LIST' },
                        ...result.ids.map(id => ({
                            type: 'OrgTrailSessionCohort',
                            id,
                        })),
                    ]
                } else {
                    return [{ type: 'OrgTrailSessionCohort', id: 'LIST' }]
                }
            },
        }),

        createNewOrgTrailSessionCohort: builder.mutation({
            query: initialOrgTrailSessionCohort => ({
                url: '/orgTrailSessionCohorts/createNewOrgTrailSessionCohort',
                method: 'POST',
                body: { ...initialOrgTrailSessionCohort },
            }),
            invalidatesTags: [
                { type: 'OrgTrailSessionCohort', id: 'LIST' },
            ],
        }),

        updateOrgTrailSessionCohort: builder.mutation({
            query: initialOrgTrailSessionCohort => ({
                url: '/orgTrailSessionCohorts/updateOrgTrailSessionCohort',
                method: 'PATCH',
                body: { ...initialOrgTrailSessionCohort },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'OrgTrailSessionCohort', id: arg.id },
                { type: 'OrgTrailSessionCohort', id: 'LIST' },
            ],
        }),

        deleteOrgTrailSessionCohort: builder.mutation({
            query: ({ id, deleteMode = 'soft' }) => ({
                url: '/orgTrailSessionCohorts/deleteOrgTrailSessionCohort',
                method: 'DELETE',
                body: { id, deleteMode },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'OrgTrailSessionCohort', id: arg.id },
                { type: 'OrgTrailSessionCohort', id: 'LIST' },
            ],
        }),
        // 15 Jun 2026
        createDefaultCohortsForOrgTrailUsers: builder.mutation({
            query: () => ({
                url: '/orgTrailSessionCohorts/createDefaultCohortsForOrgTrailUsers',
                method: 'POST',
            }),
            invalidatesTags: [
                { type: 'OrgTrailSessionCohort', id: 'LIST' },
            ],
        }),
    }),
})

export const {
    useGetOrgTrailSessionCohortsQuery,
    useCreateNewOrgTrailSessionCohortMutation,
    useUpdateOrgTrailSessionCohortMutation,
    useDeleteOrgTrailSessionCohortMutation,
    useCreateDefaultCohortsForOrgTrailUsersMutation, // 15 Jun 2026
} = orgTrailSessionCohortsApiSlice

export const selectOrgTrailSessionCohortResult =
    orgTrailSessionCohortsApiSlice.endpoints.getOrgTrailSessionCohorts.select()

const selectOrgTrailSessionCohortsData = createSelector(
    selectOrgTrailSessionCohortResult,
    orgTrailSessionCohortResult => orgTrailSessionCohortResult.data
)

export const {
    selectAll: selectAllOrgTrailSessionCohorts,
    selectById: selectOrgTrailSessionCohortById,
    selectIds: selectOrgTrailSessionCohortIds,
} = orgTrailSessionCohortsAdapter.getSelectors(
    state => selectOrgTrailSessionCohortsData(state) ?? initialState
)